import React, { useState } from 'react';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    inquiry_type: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/general-contact', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          inquiry_type: '',
          budget: '',
          timeline: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 text-white overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/c7a3ee0f1f58bb21c3e139fe2a2dc67f.jpg')`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <h1 className="text-5xl lg:text-6xl font-black leading-tight tracking-tight">
              お問い合わせ<br />
              <span className="text-blue-200 font-extrabold">経営のお悩みをお聞かせください</span>
            </h1>
            <p className="text-2xl text-blue-100 leading-relaxed font-medium max-w-4xl mx-auto">
              どんな小さなことでもお気軽にご相談ください<br />
              <span className="font-bold text-white">無料相談</span>も承っております
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#contact-form" 
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                📝 お問い合わせフォーム
              </a>
              <a 
                href="tel:03-1234-5678" 
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                📞 電話で相談する
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            <span className="text-blue-600">お問い合わせ方法</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-blue-500 text-center hover:shadow-2xl transition-shadow">
              <i className="ri-phone-line text-blue-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-6"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">お電話でのお問い合わせ</h3>
              <p className="text-3xl font-black text-blue-600 mb-4">03-1234-5678</p>
              <p className="text-gray-700 font-medium text-lg mb-6">
                受付時間：平日 9:00〜18:00<br />
                土日祝日も対応可能（要予約）
              </p>
              <a 
                href="tel:03-1234-5678"
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-bold transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 inline-block"
              >
                📞 今すぐ電話する
              </a>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-green-500 text-center hover:shadow-2xl transition-shadow">
              <i className="ri-mail-line text-green-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-6"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">メールでのお問い合わせ</h3>
              <p className="text-2xl font-bold text-green-600 mb-4">info@example.com</p>
              <p className="text-gray-700 font-medium text-lg mb-6">
                24時間受付<br />
                営業日内にご返信いたします
              </p>
              <a 
                href="mailto:info@example.com"
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-bold transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 inline-block"
              >
                📧 メールを送る
              </a>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-xl border-t-4 border-orange-500 text-center hover:shadow-2xl transition-shadow">
              <i className="ri-map-pin-line text-orange-500 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-6"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">ご訪問でのご相談</h3>
              <p className="text-gray-700 font-medium text-lg mb-6">
                〒100-0001<br />
                東京都千代田区千代田1-1-1<br />
                千代田ビル5階
              </p>
              <a 
                href="#contact-form"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-bold transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 inline-block"
              >
                📍 訪問相談を予約
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-center text-gray-900 mb-12 tracking-tight">
            よくある<span className="text-blue-600">ご質問</span>
          </h2>
          
          <div className="space-y-6">
            <div className="bg-blue-50 p-8 rounded-lg border-l-4 border-blue-500 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Q. 相談料はかかりますか？</h3>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                A. 初回相談は<span className="font-bold text-blue-600">無料</span>です。お気軽にご相談ください。サービス内容や料金についても詳しくご説明いたします。
              </p>
            </div>
            
            <div className="bg-green-50 p-8 rounded-lg border-l-4 border-green-500 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Q. どのような業種に対応していますか？</h3>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                A. <span className="font-bold text-green-600">従業員30人未満の中小企業</span>であれば、業種を問わず対応いたします。製造業、小売業、サービス業など幅広い業種での実績があります。
              </p>
            </div>
            
            <div className="bg-orange-50 p-8 rounded-lg border-l-4 border-orange-500 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Q. 遠方でも対応可能ですか？</h3>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                A. はい、<span className="font-bold text-orange-600">オンライン相談</span>も承っております。また、定期的な訪問が必要な場合は、交通費等についてご相談させていただきます。
              </p>
            </div>
            
            <div className="bg-purple-50 p-8 rounded-lg border-l-4 border-purple-500 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Q. 契約期間はどのくらいですか？</h3>
              <p className="text-gray-700 leading-relaxed text-lg font-medium">
                A. <span className="font-bold text-purple-600">6ヶ月の更新制</span>となっております。また、2ヶ月間のお試し期間もご用意しておりますので、まずはお気軽にお試しください。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900 mb-6 tracking-tight">
              <span className="text-blue-600">お問い合わせフォーム</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed font-medium">
              下記フォームにご記入の上、送信してください。<br />
              営業日内にご返信いたします。
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-xl p-8">
            {submitStatus === 'success' && (
              <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-500 text-2xl mr-3"></i>
                  <p className="text-green-700 font-semibold text-lg">
                    お問い合わせありがとうございます。営業日内にご返信いたします。
                  </p>
                </div>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 text-2xl mr-3"></i>
                  <p className="text-red-700 font-semibold text-lg">
                    送信に失敗しました。もう一度お試しいただくか、お電話でお問い合わせください。
                  </p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="general-contact-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-lg font-semibold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                    placeholder="山田太郎"
                  />
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-lg font-semibold text-gray-700 mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                    placeholder="株式会社○○"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-lg font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                    placeholder="example@company.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-lg font-semibold text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="inquiry_type" className="block text-lg font-semibold text-gray-700 mb-2">
                    お問い合わせ種別 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="inquiry_type"
                    name="inquiry_type"
                    value={formData.inquiry_type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="サービス内容について">サービス内容について</option>
                    <option value="料金について">料金について</option>
                    <option value="試し顧問について">試し顧問について</option>
                    <option value="無料相談の申し込み">無料相談の申し込み</option>
                    <option value="その他">その他</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="budget" className="block text-lg font-semibold text-gray-700 mb-2">
                    ご予算（月額）
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="5万円未満">5万円未満</option>
                    <option value="5万円〜10万円">5万円〜10万円</option>
                    <option value="10万円〜15万円">10万円〜15万円</option>
                    <option value="15万円〜20万円">15万円〜20万円</option>
                    <option value="20万円以上">20万円以上</option>
                    <option value="要相談">要相談</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="timeline" className="block text-lg font-semibold text-gray-700 mb-2">
                  開始希望時期
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg pr-8"
                >
                  <option value="">選択してください</option>
                  <option value="すぐに">すぐに</option>
                  <option value="1ヶ月以内">1ヶ月以内</option>
                  <option value="2〜3ヶ月以内">2〜3ヶ月以内</option>
                  <option value="半年以内">半年以内</option>
                  <option value="時期未定">時期未定</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-lg font-semibold text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg resize-none"
                  placeholder="現在の経営課題、ご質問、ご要望などをお聞かせください（500文字以内）"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-12 py-4 rounded-lg font-bold text-xl transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                >
                  {isSubmitting ? '送信中...' : '📝 お問い合わせを送信'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;